import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { router } from '../../services/router.js';
import { chatApi, type ChatConversation } from '../../services/chat-api.js';
import { wsClient } from '../../services/websocket-client.js';

@customElement('cp-fab-menu')
export class CpFabMenu extends LitElement {
  createRenderRoot() { return this; }

  @state() private _open = false;
  @state() private _conversations: ChatConversation[] = [];
  @state() private _voiceCallActive = false;

  connectedCallback(): void {
    super.connectedCallback();
    wsClient.addEventListener('ws-message', this._onWsMessage);
    document.addEventListener('click', this._onDocumentClick);
    void this._loadConversations();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    wsClient.removeEventListener('ws-message', this._onWsMessage);
    document.removeEventListener('click', this._onDocumentClick);
  }

  private async _loadConversations(): Promise<void> {
    try {
      const all = await chatApi.listConversations();
      this._conversations = all.slice(0, 5);
    } catch {
      this._conversations = [];
    }
  }

  private _onWsMessage = (e: Event): void => {
    const { type } = (e as CustomEvent).detail as { type: string };
    if (type === 'voice:call:started') this._voiceCallActive = true;
    if (type === 'voice:call:ended') this._voiceCallActive = false;
  };

  private _onDocumentClick = (e: Event): void => {
    if (!this._open) return;
    if (!this.contains(e.target as Node)) {
      this._open = false;
    }
  };

  private _toggleMenu(): void {
    this._open = !this._open;
    if (this._open) void this._loadConversations();
  }

  private _openNewChat(): void {
    this._open = false;
    router.navigateTo('/chat');
  }

  private _openConversation(id: number): void {
    this._open = false;
    router.navigateTo(`/chat/${id}`);
  }

  render() {
    if (this._voiceCallActive) return nothing;

    const hasConversations = this._conversations.length > 0;

    return html`
      <div class="cp-fab-menu">
        ${this._open ? html`
          <div class="cp-fab-menu__menu">
            <button class="cp-fab-menu__menu-item cp-fab-menu__menu-item--primary" @click=${this._openNewChat}>
              <span class="cp-fab-menu__item-icon">+</span>
              <span>Neuer Chat</span>
            </button>

            ${hasConversations ? html`
              <button class="cp-fab-menu__menu-item" @click=${() => this._openConversation(this._conversations[0].id)}>
                <span class="cp-fab-menu__item-icon">&#8635;</span>
                <span>Letztes Gespr&auml;ch fortsetzen</span>
              </button>
              <div class="cp-fab-menu__divider"></div>
              ${this._conversations.map(conv => html`
                <button
                  class="cp-fab-menu__menu-item cp-fab-menu__menu-item--conversation"
                  @click=${() => this._openConversation(conv.id)}
                >
                  <span class="cp-fab-menu__conversation-title">${conv.title}</span>
                </button>
              `)}
            ` : nothing}
          </div>
        ` : nothing}

        <button
          class="cp-fab-menu__btn${this._open ? ' cp-fab-menu__btn--open' : ''}"
          @click=${this._toggleMenu}
          title="Chat &ouml;ffnen"
          aria-label="Chat-Men&uuml; &ouml;ffnen"
        >
          &#128172;
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-fab-menu': CpFabMenu;
  }
}
